/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useEffect, useState, useReducer, useMemo, useRef } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Material Dashboard 2 React main context
const MaterialUI = createContext();

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      return { ...state, darkMode: action.value };
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });


/*
* CIMBEO Context Functions / State Values
*/

// Function to check if token is expired
const isTokenExpired = (expiration) => {
  console.log('Checking token expiration with value:', expiration);
  if (!expiration) {
    console.log('No expiration value provided, considering token expired');
    return true;
  }
  const now = Date.now();
  const expTime = new Date(expiration).getTime();
  console.log('Token expiration check:', {
    now,
    expiration: expTime,
    isExpired: now >= expTime,
    timeUntilExpiry: (expTime - now) / 1000 + ' seconds'
  });
  return now >= expTime;
};

// Function to check if token needs refresh
const needsTokenRefresh = (expiration) => {
  console.log('Checking if token needs refresh with value:', expiration);
  if (!expiration) {
    console.log('No expiration value provided, considering refresh needed');
    return true;
  }
  const now = Date.now();
  const expTime = new Date(expiration).getTime();
  const fiveMinutes = 10 * 60 * 1000; // 5 minutes in milliseconds
  const needsRefresh = now >= (expTime - fiveMinutes);
  console.log('Token refresh check:', {
    now,
    expiration: expTime,
    needsRefresh,
    timeUntilRefresh: ((expTime - fiveMinutes) - now) / 1000 + ' seconds'
  });
  return needsRefresh;
};

// Function to refresh token
const refreshToken = async (currentToken) => {
  try {
    const authURL = `${process.env.REACT_APP_AUTH_URL}/v1/auth/refresh`;
    console.log('Attempting to refresh token at:', authURL);
    
    const resp = await fetch(authURL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`
      },
      body: JSON.stringify({
        token: currentToken
      })
    });

    if (!resp.ok) {
      console.error('Token refresh failed with status:', resp.status, 'Response body:', data);
      throw new Error(`Failed to refresh token: ${resp.status} ${JSON.stringify(data)}`);
    }

    const data = await resp.json();

    // Validate response format
    if (!data.token || !data.expiration_time) {
      console.error('Invalid refresh response format:', data);
      return null;
    }

    return data;
  } catch (error) {
    console.error('Error refreshing token:', error);
    // Don't throw the error, just return null to indicate refresh failed
    return null;
  }
};

const CimbeoContext = createContext();

CimbeoContext.displayName = "CIMBEO Context"

// Default data
const defaultProjects = [
    {
        id: 1,
        title: "RCA 15",
        description: "The RCA15 mission challenges Special Operations Forces (SOF) to execute a precision, high-tech operation in the smart, highly surveilled urban environments of 2035. Set within the fictional nations of Adversaria and Neutralia, teams must infiltrate undetected, gather intelligence, disrupt adversary systems, and exfiltrate without drawing attention—mirroring the finesse of a cinematic heist. Operating in dense cities laced with AI surveillance, electromagnetic monitoring, and civilian sensors, operators rely on stealth technologies, digital signature management, and seamless teamwork. Each team member plays a specialized role in navigating complex terrain, ensuring mission success through innovation, discretion, and hyper-enabled coordination.",
        requirements: [],
        documents: [
            "51a0fcfd-8eb1-44fc-a3d5-c73bdaad1c8a",
            "b7442336-c224-45b7-84b5-d3ece8ab00a9"
        ],
        assetCount: 12,
        numMissionNeeds: 9,
        numCapabilities: 56,
        numConOps: 474,
        numRequirements: 1883
    },
    {
        id: 2,
        title: "RCA 14",
        description: "Innovation Foundry 14's Rapid Capability Assessment analysis.",
        requirements: [],
        documents: [],
        assetCount: 0,
        numMissionNeeds: 0,
        numCapabilities: 0,
        numConOps: 0,
        numRequirements: 0
    },
    {
        id: 3,
        title: "RCA 13",
        description: "Innovation Foundry 13's Rapid Capability Assessment analysis.",
        requirements: [],
        documents: [],
        assetCount: 0,
        numMissionNeeds: 0,
        numCapabilities: 0,
        numConOps: 0,
        numRequirements: 0
    },
    {
        id: 4,
        title: "RCA 12",
        description: "Innovation Foundry 12's Rapid Capability Assessment analysis.",
        requirements: [],
        documents: [],
        assetCount: 0,
        numMissionNeeds: 0,
        numCapabilities: 0,
        numConOps: 0,
        numRequirements: 0
    }
];

const defaultUsers = [
    {
        id: 1,
        username: "brian.sisco",
        realName: "Brian Sisco",
        organization: "Willowview Consulting",
        email: "b.sisco@willowview.us",
        role: "Admin",
        isDBUser: true,
        projectIds: [1, 2, 3, 4]
    },
    {
        id: 2,
        username: "janedoe",
        realName: "Jane Doe",
        organization: "Tech Inc",
        email: "jane.doe@tech.com",
        role: "Manager",
        isDBUser: false,
        projectIds: [1]
    },
    {
        id: 3,
        username: "bobsmith",
        realName: "Bob Smith",
        organization: "Data Corp",
        email: "bob.smith@data.com",
        role: "User",
        isDBUser: false,
        projectIds: [1, 2]
    },
    {
        id: 4,
        username: "johndoe",
        realName: "John Doe",
        organization: "Acme Corp",
        email: "john.doe@acme.com",
        role: "User",
        isDBUser: true,
        projectIds: [2, 3, 4]
    }
];

const defaultDocuments = [
    {
        id: "51a0fcfd-8eb1-44fc-a3d5-c73bdaad1c8a",
        name: "RCA 15 Read Ahead Final _ Mission Focus Area Overview.pdf",
        type: "requirements",
        status: "complete",
        completedSteps:["importing","normalizing","summarizing","contentAnalysis","contentCleaning","missionNeed","capability","conops","requirements"],
        projectName: "RCA 15",
        timestamp: "2025-03-28T09:12:38Z",
        uploadedBy: "Brian Sisco",
        currentStep: null,
        stepStartTime: null,
    },
    {
        id: "b7442336-c224-45b7-84b5-d3ece8ab00a9",
        name: "RCA 15 Read Ahead Final _ Signature Management.pdf",
        type: "requirements",
        status: "complete",
        completedSteps:["importing","normalizing","summarizing","contentAnalysis","contentCleaning","missionNeed","capability","conops","requirements"],
        projectName: "RCA 15",
        timestamp: "2025-03-28T09:26:07Z",
        uploadedBy: "Brian Sisco",
        currentStep: null,
        stepStartTime: null,
    }
];

// Check if we need to update localStorage - this is a temporary solution and was added to force a refresh of the local storage
const storedProjects = localStorage.getItem('cimbeo_projects');
const storedAllUsers = localStorage.getItem('cimbeo_allUsers');
const storedDocuments = localStorage.getItem('cimbeo_ingestedDocuments');

const shouldUpdateStorage = !storedProjects || 
    !storedAllUsers || 
    !storedDocuments ||
    (() => {
        try {
            const parsedProjects = JSON.parse(storedProjects);
            // Check if all default projects exist in stored projects
            return !defaultProjects.every(defaultProject => {
                const storedProject = parsedProjects.find(p => p.id === defaultProject.id);
                if (!storedProject) return false;
                
                return defaultProject.title === storedProject.title &&
                       defaultProject.id === storedProject.id &&
                       defaultProject.assetCount === storedProject.assetCount &&
                       defaultProject.numMissionNeeds === storedProject.numMissionNeeds &&
                       defaultProject.numCapabilities === storedProject.numCapabilities &&
                       defaultProject.numConOps === storedProject.numConOps &&
                       defaultProject.numRequirements === storedProject.numRequirements;
            });
        } catch (e) {
            return true;
        }
    })();

if (shouldUpdateStorage) {
    // Update localStorage with default data
    localStorage.setItem('cimbeo_projects', JSON.stringify(defaultProjects));
    localStorage.setItem('cimbeo_allUsers', JSON.stringify(defaultUsers));
    localStorage.setItem('cimbeo_ingestedDocuments', JSON.stringify(defaultDocuments));
}

function CimbeoProvider({children}) {
    // Initialize state from localStorage or defaults
    const [projects, setProjects] = useState(() => {
        const savedProjects = localStorage.getItem('cimbeo_projects');
        return savedProjects ? JSON.parse(savedProjects) : defaultProjects;
    });

    const [allUsers, setAllUsers] = useState(() => {
        const savedUsers = localStorage.getItem('cimbeo_allUsers');
        return savedUsers ? JSON.parse(savedUsers) : defaultUsers;
    });

    const [user, setUser] = useState(() => {
        const savedUser = localStorage.getItem('cimbeo_user');
        return savedUser ? JSON.parse(savedUser) : "";
    });

    const [projectName, setProjectName] = useState(() => {
        return localStorage.getItem('cimbeo_projectName') || "";
    });

    const [projectId, setProjectId] = useState(() => {
        const savedId = localStorage.getItem('cimbeo_projectId');
        return savedId ? Number(savedId) : null;
    });

    const [ingestedDocuments, setIngestedDocuments] = useState(() => {
        const savedDocuments = localStorage.getItem('cimbeo_ingestedDocuments');
        return savedDocuments ? JSON.parse(savedDocuments) : defaultDocuments;
    });

    const [userToken, setUserToken] = useState(() => {
        const savedToken = localStorage.getItem('cimbeo_token');
        const savedExpiration = localStorage.getItem('cimbeo_token_expiration');
        
        // Check if token exists and is not expired
        if (savedToken && savedExpiration) {
            if (!isTokenExpired(savedExpiration)) {
                return savedToken;
            }
            // Clear expired token
            localStorage.removeItem('cimbeo_token');
            localStorage.removeItem('cimbeo_token_expiration');
        }
        return "";
    });

    // Add inactivity timeout state
    const [lastActivity, setLastActivity] = useState(Date.now());
    const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 10 minutes in milliseconds

    // Add useRef for interval and last activity
    const intervalRef = useRef(null);
    const lastActivityRef = useRef(Date.now());

    // Update lastActivityRef whenever lastActivity changes
    useEffect(() => {
        lastActivityRef.current = lastActivity;
    }, [lastActivity]);

    // Combine activity tracking and token checking into a single effect
    useEffect(() => {
        const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        
        const handleUserActivity = () => {
            const now = Date.now();
            lastActivityRef.current = now;
            setLastActivity(now);
        };

        const checkTokenStatus = async () => {
            const savedToken = localStorage.getItem('cimbeo_token');
            const savedExpiration = localStorage.getItem('cimbeo_token_expiration');

            if (!savedToken || !savedExpiration) {
                console.log('No token or expiration found, logging out');
                handleLogout();
                return;
            }

            // Check if user has been inactive using the ref value
            const currentActivity = lastActivityRef.current;
            if (Date.now() - currentActivity > INACTIVITY_TIMEOUT) {
                console.log('User inactive, logging out');
                handleLogout();
                return;
            }

            // First check if we need to refresh
            if (needsTokenRefresh(savedExpiration)) {
                console.log('Token needs refresh, attempting refresh');
                const response = await refreshToken(savedToken);
                
                if (response && response.token && response.expiration_time) {
                    console.log('Token refresh successful, updating token');
                    setToken({
                        token: response.token,
                        expiration: response.expiration_time
                    });
                    return;
                } else {
                    console.log('Token refresh failed, but token is not expired yet, continuing with current token');
                    // Only logout if the token is actually expired
                    if (isTokenExpired(savedExpiration)) {
                        console.log('Token is expired, logging out');
                        handleLogout();
                    }
                    return;
                }
            }

            // Then check if it's expired
            if (isTokenExpired(savedExpiration)) {
                console.log('Token is expired, logging out');
                handleLogout();
                return;
            }
        };

        // Add event listeners
        activityEvents.forEach(event => {
            window.addEventListener(event, handleUserActivity);
        });

        // Clear any existing interval
        if (intervalRef.current) {
            clearInterval(intervalRef.current);
        }

        // Set up new interval
        intervalRef.current = setInterval(checkTokenStatus, 4 * 60 * 1000);
        
        // Initial check
        checkTokenStatus();

        return () => {
            // Clean up event listeners
            activityEvents.forEach(event => {
                window.removeEventListener(event, handleUserActivity);
            });
            // Clear interval
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, []); // Empty dependency array since we're using refs

    // Function to handle logout
    const handleLogout = () => {
        setToken(null);
        setUser("");
        setProjectName("");
        setProjectId(null);
        // Clear any other user-specific state here
    };

    // Function to set token and its expiration
    const setToken = (tokenData) => {
        console.log('Setting token with data:', tokenData);
        
        if (!tokenData) {
            console.log('No token data provided, clearing token');
            localStorage.removeItem('cimbeo_token');
            localStorage.removeItem('cimbeo_token_expiration');
            setUserToken("");
            return;
        }

        if (!tokenData.token) {
            console.log('No token in data, clearing token');
            localStorage.removeItem('cimbeo_token');
            localStorage.removeItem('cimbeo_token_expiration');
            setUserToken("");
            return;
        }

        if (!tokenData.expiration) {
            console.log('No expiration in data, clearing token');
            localStorage.removeItem('cimbeo_token');
            localStorage.removeItem('cimbeo_token_expiration');
            setUserToken("");
            return;
        }

        // Store token and expiration
        console.log('Storing token and expiration:', {
            token: tokenData.token,
            expiration: tokenData.expiration
        });
        localStorage.setItem('cimbeo_token', tokenData.token);
        localStorage.setItem('cimbeo_token_expiration', tokenData.expiration);
        setUserToken(tokenData.token);
    };

    // Persist state changes to localStorage
    useEffect(() => {
        localStorage.setItem('cimbeo_user', JSON.stringify(user));
    }, [user]);

    useEffect(() => {
        localStorage.setItem('cimbeo_projectName', projectName);
    }, [projectName]);

    useEffect(() => {
        localStorage.setItem('cimbeo_projectId', projectId);
    }, [projectId]);

    useEffect(() => {
        localStorage.setItem('cimbeo_projects', JSON.stringify(projects));
    }, [projects]);

    useEffect(() => {
        localStorage.setItem('cimbeo_ingestedDocuments', JSON.stringify(ingestedDocuments));
    }, [ingestedDocuments]);

    useEffect(() => {
        localStorage.setItem('cimbeo_allUsers', JSON.stringify(allUsers));
    }, [allUsers]);

    return (
        <CimbeoContext.Provider value={{
            user, setUser,  
            projectName, setProjectName,
            projectId, setProjectId,
            projects, setProjects,
            ingestedDocuments, setIngestedDocuments,
            allUsers, setAllUsers,
            userToken, setToken,
            handleLogout
        }}>
            {children}
        </CimbeoContext.Provider>
    );
}

function useCimbeo() {
    return useContext(CimbeoContext);
}

export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  CimbeoProvider,
  useCimbeo,
};